import { useMemo, useState, memo, useCallback } from 'react'
import {
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  FormHelperText,
  OutlinedInput,
  InputAdornment,
  Divider,
} from '@mui/material'
import { format, differenceInMinutes } from 'date-fns'
import { HighlightOff, AccessTimeOutlined, AddCommentOutlined } from '@mui/icons-material'
import { getFieldId } from '../utils/utils'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { useProcessContext } from '../../../store'
import TimeStampedEntriesList from '../../../components/common/TimeStampedEntriesList'
import { STANDARD_COLORS } from '../../../utility/constants'

const TimeStampedBigTextField = ({
  id,
  field,
  value = [],
  label = null,
  onChange,
  className = '',
  helperText = null,
  disabled = false,
  autoFocus = false,
  inputSx = {},
  textColor = null,
  ...props
}) => {
  const { user } = useAuthContext()
  const usersList = useProcessContext((state) => state.processContext?.dynamicListData?.usersList)

  const [modalOpen, setModalOpen] = useState(false)
  const [newText, setNewText] = useState('')

  const getUsername = useCallback(
    (userId) => {
      const user = Array.isArray(usersList) ? usersList.find((u) => u.id === userId) : null
      return user?.value?.replace(/\s*\([^)]*\)\s*/g, '').trim() ?? ''
    },
    [usersList],
  )

  const latestEntry = useMemo(() => (value && value[0] ? value[0] : null), [value])

  const username = useMemo(
    () => (latestEntry ? getUsername(latestEntry.user_id) : null),
    [latestEntry, getUsername],
  )

  const isWithinEditWindow = (timestamp) => {
    try {
      return differenceInMinutes(new Date(), new Date(timestamp)) < 5
    } catch {
      return false
    }
  }

  const canEdit = (entry) =>
    !!entry && entry.user_id === user?.id && isWithinEditWindow(entry.timestamp)

  const handleModalOpen = () => {
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setNewText('')
  }

  const handleAddUpdate = () => {
    if (newText.trim()) {
      const timestamp = format(new Date(), 'dd MMM yyyy, h:mm a')
      const newEntry = { timestamp, text: newText, user_id: user?.id }
      const newValue = [newEntry, ...value]

      onChange({ target: { name: getFieldId(id), value: newValue } })
      setNewText('')
    }
  }

  const handleEditUpdate = (index, newText) => {
    const trimmed = (newText || '').trim()
    if (!trimmed) return
    const updatedValue = [...value]
    updatedValue[index] = { ...updatedValue[index], text: trimmed }
    onChange({ target: { name: getFieldId(id), value: updatedValue } })
  }

  const handleDeleteUpdate = (index) => {
    const updatedValue = value.filter((_, i) => i !== index)
    onChange({ target: { name: getFieldId(id), value: updatedValue } })
  }

  return (
    <>
      <FormControl
        fullWidth
        size="small"
        variant="outlined"
        error={helperText != null}
        sx={{ mb: 0.5 }}
        className={className}
      >
        <InputLabel htmlFor={id}>{label}</InputLabel>
        <OutlinedInput
          id={id}
          name={id}
          value=""
          label={label}
          color="primary"
          readOnly
          onClick={handleModalOpen}
          autoFocus={autoFocus}
          multiline
          maxRows={2}
          sx={{
            cursor: 'pointer',
            backgroundColor: disabled ? STANDARD_COLORS.BG_READONLY : STANDARD_COLORS.BG_EDITABLE,
            fontSize: '12px',
            minHeight: 40,
            py: 1,
            '& .MuiOutlinedInput-input': {
              cursor: 'pointer',
              fontSize: '12px',
              height: 0, // hide input but preserve space
              padding: 0,
              m: 0,
              border: 'none',
              visibility: 'hidden', // hide visually but not collapse
            },
            '&:hover': !disabled ? { backgroundColor: 'rgba(0, 0, 0, 0.02)' } : {},
            ...inputSx,
          }}
          startAdornment={
            latestEntry ? (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  padding: '0 38px 0 8px',
                  overflow: 'hidden',
                }}
              >
                {/* Timestamp */}
                <Typography
                  component="span"
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    mr: 1,
                    flexShrink: 0,
                    color: textColor || 'text.primary',
                  }}
                >
                  {`${latestEntry.timestamp}:`}
                </Typography>

                {/* Text + Username */}
                <Typography
                  component="span"
                  sx={{
                    fontSize: '12px',
                    color: textColor || 'text.primary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {latestEntry.text}
                </Typography>
                {latestEntry.user_id && username && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '11px',
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      ml: 0.5,
                      paddingRight: 0.5,
                      color: textColor || 'text.primary',
                    }}
                  >
                    - {username}
                  </Typography>
                )}
                {
                  /* Show count of additional entries if any */
                  value.length > 1 ? (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        ml: 0.5,
                        paddingRight: 0.5,
                        color: textColor || 'text.primary',
                      }}
                    >
                      {`(+${value.length - 1} more)`}
                    </Typography>
                  ) : null
                }
              </div>
            ) : null
          }
          endAdornment={
            latestEntry && (
              <InputAdornment position="end" sx={{ alignSelf: 'center' }}>
                <AccessTimeOutlined sx={{ fontSize: 16, color: 'action.disabled' }} />
              </InputAdornment>
            )
          }
          {...props}
        />

        {helperText && <FormHelperText error>{helperText}</FormHelperText>}
      </FormControl>

      {/* Modal for viewing and adding entries */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 600,
            color: '#27104e',
            pb: 1,
          }}
        >
          {label}
          <IconButton onClick={handleModalClose} size="small">
            <HighlightOff />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {/* Add New Entry Section */}
          {!disabled && (
            <>
              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  backgroundColor: '#FCFBFF',
                  border: '1px solid #DDE1F3',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#656D90',
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <AddCommentOutlined sx={{ fontSize: '16px' }} />
                  Add New Entry
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Write your entry here..."
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleAddUpdate()
                    }
                  }}
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      fontSize: '12px',
                      '& fieldset': {
                        borderColor: '#DDE1F3',
                      },
                    },
                  }}
                  color="primary"
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setNewText('')}
                    sx={{
                      fontSize: '11px',
                      textTransform: 'none',
                      borderColor: '#DDE1F3',
                      color: '#656D90',
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAddUpdate}
                    disabled={!newText.trim()}
                    sx={{
                      fontSize: '11px',
                      textTransform: 'none',
                    }}
                  >
                    Add Entry
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />
            </>
          )}

          {/* Entries List */}
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#656D90',
              mb: 2,
            }}
          >
            All Entries {value.length > 0 && `(${value.length})`}
          </Typography>

          {value.length > 0 ? (
            <TimeStampedEntriesList
              entries={value}
              editablePredicate={(entry) => !disabled && canEdit(entry)}
              onEdit={handleEditUpdate}
              onDelete={handleDeleteUpdate}
              resolveUserName={(entry) => getUsername(entry.user_id)}
            />
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                color: '#999',
              }}
            >
              <Typography sx={{ fontSize: '12px', color: '#656A7E' }}>
                No entries yet. Add your first entry above!
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default memo(
  TimeStampedBigTextField,
  (prevProps, nextProps) =>
    prevProps.value === nextProps.value && prevProps.disabled === nextProps.disabled,
)
